import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { R, format } from '@/features/profile/resources/ProfileResources.resx';
import { RadioButton, toast, VerticalContainer } from '@/components';
import { LanguageCode } from '@/shared/enums';
import { ProfilePaths } from '@/features/profile/model/types';
import { toggleLanguage } from '@/features/profile/widgets/language/api/toggleLanguage';

export type LanguageProps = {
    supportedLanguages: LanguageCode[];
    currentLanguage: LanguageCode;
};

const languageOptions = [
    { code: LanguageCode.da, label: R.Danish },
    { code: LanguageCode.nb, label: R.Norwegian },
    { code: LanguageCode.en, label: R.English },
];

export const Language = ({
    supportedLanguages,
    currentLanguage,
}: LanguageProps) => {
    const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);

    const updateLanguageMutation = useMutation({
        mutationFn: toggleLanguage,
        onSuccess: () => {
            window.location.reload();
        },
        onError: (err) => {
            setSelectedLanguage(currentLanguage);
            toast.addToast({
                type: 'error',
                message: format(R.NetworkError, err.message),
                autoClose: true,
            });
        },
    });

    const changeLanguage = (languageCode: LanguageCode) => {
        if (languageCode === selectedLanguage) return;
        setSelectedLanguage(languageCode);
        updateLanguageMutation.mutate(languageCode);
    };

    const visibleOptions = languageOptions.filter((option) =>
        supportedLanguages.includes(option.code),
    );

    return (
        <VerticalContainer spacing="md">
            <h2 id={`heading-${ProfilePaths.Language}`}>{R.Language}</h2>
            <div id="language-description" className="max-w-prose">
                {R.ChoosePreferedLanguage}
            </div>

            <VerticalContainer
                spacing="none"
                role="radiogroup"
                aria-labelledby={`heading-${ProfilePaths.Language}`}
                aria-describedby="language-description"
            >
                {visibleOptions.map((option) => (
                    <RadioButton
                        key={option.code}
                        id={`language-${LanguageCode[option.code]}`}
                        name="languageOption"
                        value={LanguageCode[option.code]}
                        label={option.label}
                        checked={selectedLanguage === option.code}
                        disabled={updateLanguageMutation.isPending}
                        onChange={() => changeLanguage(option.code)}
                        containerClassName="p-2"
                    />
                ))}
            </VerticalContainer>
        </VerticalContainer>
    );
};
